/**
 * LocationDrawer — right-side pop-out with a single store's full QSR profile:
 * rank, headline KPIs, animated trend, day-part speed bars and an AI note.
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Timer, DollarSign, Target, Gauge, Sparkles, TrendingUp, TrendingDown, MapPin } from 'lucide-react';
import Sparkline from './Sparkline';
import ChannelProfit from './ChannelProfit';
import { DAYPARTS, TARGETS, avgBy, money } from './qsrDemoData';

export default function LocationDrawer({ store, open, onOpenChange }) {
  if (!store) return null;
  const netSplh = avgBy('splh');
  const netSos = avgBy('sos');
  const vsNet = store.splh - netSplh;
  const maxSos = Math.max(...store.sosByDaypart);

  const kpis = [
    { icon: DollarSign, label: 'SPLH', value: `$${store.splh.toFixed(1)}`, good: store.splh >= TARGETS.splh, color: '#10b981' },
    { icon: Timer, label: 'Speed of service', value: `${store.sos.toFixed(1)}m`, good: store.sos <= TARGETS.sos, color: '#3b82f6' },
    { icon: Target, label: 'Order accuracy', value: `${store.accuracy.toFixed(1)}%`, good: store.accuracy >= TARGETS.accuracy, color: '#8b5cf6' },
    { icon: Gauge, label: 'Labour %', value: `${store.labourPct.toFixed(1)}%`, good: store.labourPct <= TARGETS.labourPct, color: '#f59e0b' },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="location-drawer">
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6">
          <div className="flex items-center gap-2 text-emerald-300 text-xs font-medium mb-1">
            <MapPin className="w-3.5 h-3.5" /> Store profile
          </div>
          <SheetHeader className="space-y-0 text-left">
            <SheetTitle className="text-white text-2xl">{store.name}</SheetTitle>
            <SheetDescription className="text-white/70 text-xs">Store performance detail · SPLH, speed of service, waste and day-part trends</SheetDescription>
          </SheetHeader>
          <div className="text-sm text-slate-300 mt-1">Store manager · {store.manager}</div>
          <div className={`mt-3 inline-flex items-center gap-1 text-sm font-semibold ${vsNet >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {vsNet >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {vsNet >= 0 ? '+' : ''}{vsNet.toFixed(1)} SPLH vs network
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-2xl qsr-card p-3" data-testid={`drawer-kpi-${k.label.split(' ')[0].toLowerCase()}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium"><k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />{k.label}</div>
                <div className="text-xl font-semibold text-slate-900 mt-1">{k.value}</div>
                <div className={`text-[10px] font-medium mt-0.5 ${k.good ? 'text-emerald-600' : 'text-amber-600'}`}>{k.good ? 'On target' : 'Below target'}</div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl qsr-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-900">SPLH trend · 12 wks</div>
              <div className="text-xs text-slate-500">Weekly sales {money(store.weeklySales)}</div>
            </div>
            <Sparkline data={store.splhTrend} color="#10b981" width={320} height={56} className="w-full" />
          </div>

          <div className="rounded-2xl qsr-card p-4">
            <div className="text-sm font-semibold text-slate-900 mb-3">Speed of service by day-part</div>
            <div className="space-y-2">
              {DAYPARTS.map((d, i) => {
                const v = store.sosByDaypart[i];
                const bad = v > TARGETS.sos;
                return (
                  <div key={d} className="flex items-center gap-2">
                    <span className="w-14 text-[11px] text-slate-500">{d}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-[width] duration-700 ${bad ? 'bg-rose-400' : 'bg-emerald-400'}`} style={{ width: `${(v / maxSos) * 100}%` }} />
                    </div>
                    <span className={`w-10 text-right text-[11px] font-semibold ${bad ? 'text-rose-600' : 'text-slate-700'}`}>{v.toFixed(1)}m</span>
                  </div>
                );
              })}
            </div>
          </div>

          <ChannelProfit sales={store.weeklySales} mixPct={store.delivery.mix} subtitle={`${store.name} · weekly`} showSlider />

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
            <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed">
              <span className="font-semibold text-slate-900">Corvana AI:</span> {store.name}'s dinner day-part runs at
              {' '}{store.sosByDaypart[4].toFixed(1)}m — the network's slowest window. Adding one runner 5–8pm could pull it
              toward {netSos.toFixed(1)}m and lift accuracy above {TARGETS.accuracy}%. Waste is {store.wastePct}% (target &lt;2.5%).
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
